import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ArrowDown, ArrowUp, Loader2, Pencil, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SearchableSelect } from '@/components/ui/searchable-select'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { formsApi } from '@/api/formsApi'
import { versionLabel } from '@/lib/versioning'
import { describeMappingTarget, mappingFieldOptions } from '@/lib/mappingTargets'
import { RESULTS_DESTINATION_LABEL, type ResultsDestination } from '@/api/integrationsApi'
import { LisPdfMappingSection } from '@/components/integrations/LisPdfMappingSection'
import { MappingProviderFields } from '@/components/integrations/MappingProviderFields'
import { providerChanged, providerDraftBody, providerDraftOf, providerProblem } from '@/lib/lis/orderingProvider'
import type { LisResultMapping, LisResultMappingRow, LisResultSourceType } from '@/api/lisResultsApi'
import {
  FLAG_RULE_OPTIONS,
  VALUE_TYPE_OPTIONS,
  RESULT_CODE_PATTERN,
  emptyRowDraft,
  flagRuleLabel,
  moveRow,
  rowDraftBody,
  rowDraftErrors,
  rowDraftFrom,
  type ResultRowDraft,
} from '@/lib/lis/resultMapping'
import {
  useAddLisResultRow,
  useCreateLisResultMapping,
  useLisPdfMappings,
  useLisProductResultFields,
  useLisResultMappings,
  useRemoveLisResultMapping,
  useRemoveLisResultRow,
  useReorderLisResultRows,
  useUpdateLisResultMapping,
  useUpdateLisResultRow,
} from '@/hooks/useLisResults'

type OutgoingType = 'results' | 'pdf'

/** The "new mapping" choice in the mapping picker. */
const NEW_MAPPING = '__new__'

/**
 * Outgoing Mapping (z8nrz7ffzv AC2): which form or product fields this LIS
 * integration releases to Epic as which result lines, under which order code.
 *
 * One release = one result message: every row with a value becomes one OBX,
 * under the OBR of the mapping's order code. Results go UNSOLICITED (MedStar):
 * nothing is ordered first, so the code is typed here, not taken from LIS
 * Stages. The destination is the integration's, shown here and changed on Edit.
 */
export function LisOutgoingMappingTab({
  integrationId,
  destination,
}: {
  integrationId: string
  destination: ResultsDestination
}) {
  const [type, setType] = useState<OutgoingType>('results')
  // A mapping's id, NEW_MAPPING, or '' for none chosen.
  const [selected, setSelected] = useState('')

  const mappings = useLisResultMappings(integrationId)
  const list = mappings.data ?? []
  const mapping = list.find((m) => m.id === selected) ?? null
  const mappedCodes = new Set(list.map((m) => m.testCode))
  const pdfMappings = useLisPdfMappings(integrationId)
  const pdfCodes = (pdfMappings.data ?? []).map((m) => m.testCode)

  return (
    <div className='space-y-4'>
      <p className='text-sm text-muted-foreground'>
        Select what this integration sends out to{' '}
        <strong>{RESULTS_DESTINATION_LABEL[destination]}</strong>.
      </p>

      <div className='inline-flex rounded-md border p-0.5' role='group' aria-label='Outgoing type'>
        <SegmentButton active={type === 'results'} onClick={() => setType('results')}>
          Test results
        </SegmentButton>
        <SegmentButton active={type === 'pdf'} onClick={() => setType('pdf')}>
          PDF
        </SegmentButton>
      </div>

      {type === 'pdf' ? (
        <LisPdfMappingSection integrationId={integrationId} resultTests={[...mappedCodes]} />
      ) : (
        <>
          <p className='text-xs text-muted-foreground'>
            One release = one result message. Every row below with a value becomes one result line (OBX), under the
            order code (OBR) set here. Nothing is ordered first: Title21 makes the order number when it releases.
          </p>

          <div className='rounded-xl border bg-white p-4 shadow-sm'>
            <h3 className='text-sm font-medium'>Order</h3>
            <p className='mb-3 text-xs text-muted-foreground'>
              The order code the results are filed under in Epic, and what describes the specimen. Sent once per
              message, in the OBR.
            </p>
            <div className='grid max-w-md gap-1.5'>
              <Label htmlFor='lis-out-mapping'>Results mapping</Label>
              <Select value={selected} onValueChange={setSelected}>
                <SelectTrigger id='lis-out-mapping'>
                  <SelectValue placeholder={mappings.isPending ? 'Loading…' : 'Select a mapping'} />
                </SelectTrigger>
                <SelectContent>
                  {list.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.testCode}
                      {m.testName ? ` — ${m.testName}` : ''}
                    </SelectItem>
                  ))}
                  <SelectItem value={NEW_MAPPING}>+ New results mapping</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {selected ? (
              <TestSettings
                key={selected}
                integrationId={integrationId}
                mapping={mapping}
                takenCodes={[...list.filter((m) => m.id !== mapping?.id).map((m) => m.testCode), ...pdfCodes]}
                onCreated={(id) => setSelected(id)}
                onRemoved={() => setSelected('')}
              />
            ) : null}
          </div>

          {mapping ? <ResultRows mapping={mapping} /> : null}
        </>
      )}
    </div>
  )
}

function SegmentButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type='button'
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'rounded px-3 py-1 text-sm',
        active ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  )
}

/** The order code and name (OBR-4), form, and specimen source (OBR-15) of one mapping. */
function TestSettings({
  integrationId,
  mapping,
  takenCodes,
  onCreated,
  onRemoved,
}: {
  integrationId: string
  mapping: LisResultMapping | null
  /** Codes other mappings on this integration use, results or PDF. */
  takenCodes: string[]
  onCreated: (id: string) => void
  onRemoved: () => void
}) {
  const [code, setCode] = useState(mapping?.testCode ?? '')
  const [name, setName] = useState(mapping?.testName ?? '')
  const [formId, setFormId] = useState(mapping?.formDefinitionId ?? '')
  const [specCode, setSpecCode] = useState(mapping?.specimenSourceCode ?? '')
  const [specDesc, setSpecDesc] = useState(mapping?.specimenSource ?? '')
  const [provider, setProvider] = useState(() => providerDraftOf(mapping?.orderingProvider))

  const { data: defsResult } = useQuery({
    queryKey: ['form-definitions', 'for-lis-mapping'],
    queryFn: () => formsApi.listDefinitions(),
  })
  const definitions = defsResult?.items ?? []
  const { data: selectedForm } = useQuery({
    queryKey: ['form-definition', formId],
    enabled: !!formId,
    queryFn: () => formsApi.getDefinition(formId),
  })

  const create = useCreateLisResultMapping()
  const update = useUpdateLisResultMapping()
  const remove = useRemoveLisResultMapping()
  const error = create.error ?? update.error ?? remove.error
  const pending = create.isPending || update.isPending

  const specTooLong = specCode.trim().length > 20
  const providerInvalid = providerProblem(provider) !== null
  const codeError = !RESULT_CODE_PATTERN.test(code.trim())
    ? '1–20 letters or digits, e.g. TBNK.'
    : takenCodes.includes(code.trim())
      ? `${code.trim()} is already used by another mapping on this integration.`
      : null
  const dirty =
    !mapping ||
    code.trim() !== mapping.testCode ||
    name.trim() !== (mapping.testName ?? '') ||
    formId !== mapping.formDefinitionId ||
    specCode.trim() !== (mapping.specimenSourceCode ?? '') ||
    specDesc.trim() !== (mapping.specimenSource ?? '') ||
    providerChanged(provider, mapping.orderingProvider)

  const save = () => {
    const body = {
      testCode: code.trim(),
      testName: name.trim() || null,
      formDefinitionId: formId,
      specimenSourceCode: specCode.trim() || null,
      specimenSource: specDesc.trim() || null,
      orderingProvider: providerDraftBody(provider),
    }
    if (mapping) update.mutate({ id: mapping.id, body })
    else create.mutate({ ...body, integrationId }, { onSuccess: (created) => onCreated(created.id) })
  }

  return (
    <div className='mt-4 space-y-3 border-t pt-4'>
      <div className='grid gap-3 sm:grid-cols-[180px_1fr]'>
        <div className='grid gap-1.5'>
          <Label htmlFor='lis-out-code'>Order code (OBR-4)</Label>
          <Input id='lis-out-code' value={code} onChange={(e) => setCode(e.target.value)} placeholder='e.g. TBNK' className='font-mono' />
          {code && codeError ? <p className='text-xs text-destructive'>{codeError}</p> : null}
        </div>
        <div className='grid gap-1.5'>
          <Label htmlFor='lis-out-name'>
            Order name <span className='font-normal text-muted-foreground'>(optional)</span>
          </Label>
          <Input id='lis-out-name' value={name} onChange={(e) => setName(e.target.value)} placeholder='e.g. TBNK PANEL' />
        </div>
      </div>
      <p className='-mt-1 text-[11px] text-muted-foreground'>
        Must be built in Epic. Nothing is ordered first: Title21 makes the order number when it releases.
      </p>
      <div className='grid gap-3 sm:grid-cols-3'>
        <div className='grid gap-1.5'>
          <Label>Form the values come from</Label>
          <SearchableSelect
            value={formId}
            onChange={setFormId}
            options={definitions.map((d) => ({
              value: d.id,
              label: `${d.name} (${d.versionName ?? versionLabel(d.version)})`,
            }))}
            selectedOption={
              formId && selectedForm
                ? { value: formId, label: `${selectedForm.name} (${selectedForm.versionName ?? versionLabel(selectedForm.version)})` }
                : undefined
            }
            placeholder='Select a form'
          />
        </div>
        <div className='grid gap-1.5'>
          <Label htmlFor='lis-out-spec-code'>
            Specimen source code <span className='font-normal text-muted-foreground'>(optional)</span>
          </Label>
          <Input
            id='lis-out-spec-code'
            value={specCode}
            onChange={(e) => setSpecCode(e.target.value)}
            placeholder='e.g. HCT/P'
            className='font-mono'
          />
          {specTooLong ? <p className='text-xs text-destructive'>1–20 characters.</p> : null}
        </div>
        <div className='grid gap-1.5'>
          <Label htmlFor='lis-out-spec-desc'>
            Specimen source description <span className='font-normal text-muted-foreground'>(optional)</span>
          </Label>
          <Input
            id='lis-out-spec-desc'
            value={specDesc}
            onChange={(e) => setSpecDesc(e.target.value)}
            placeholder='e.g. HPC Apheresis'
          />
        </div>
      </div>

      <MappingProviderFields idPrefix='lis-out-provider' value={provider} onChange={setProvider} />

      <p className='text-xs text-muted-foreground'>
        Automatically, at release: the patient of the product&apos;s procedure (PID), the patient&apos;s latest visit
        (PV1), a new order number (ORC-2) and the result status chosen (OBR-25). Not configured here.
      </p>

      {error ? <p className='text-sm text-destructive'>{error.message}</p> : null}

      <div className='flex flex-wrap gap-2'>
        <Button size='sm' onClick={save} disabled={!formId || !!codeError || specTooLong || providerInvalid || pending || !dirty}>
          {pending ? <Loader2 className='mr-1 h-4 w-4 animate-spin' /> : null}
          {mapping ? 'Save test settings' : 'Create mapping'}
        </Button>
        {mapping ? (
          <Button
            size='sm'
            variant='outline'
            disabled={remove.isPending}
            onClick={() => {
              if (!window.confirm(`Remove the outgoing mapping for ${mapping.testCode} and all its rows?`)) return
              remove.mutate(mapping.id, { onSuccess: onRemoved })
            }}
          >
            Remove mapping
          </Button>
        ) : null}
      </div>
    </div>
  )
}

/** The result rows (OBX), in the order they are sent. */
function ResultRows({ mapping }: { mapping: LisResultMapping }) {
  const [editing, setEditing] = useState<'new' | string | null>(null)
  const reorder = useReorderLisResultRows()
  const removeRow = useRemoveLisResultRow()

  const { data: form } = useQuery({
    queryKey: ['form-definition', mapping.formDefinitionId],
    queryFn: () => formsApi.getDefinition(mapping.formDefinitionId),
  })
  const productFields = useLisProductResultFields()

  const sourceLabel = (row: Pick<LisResultMappingRow, 'sourceType' | 'sourceField'>) =>
    row.sourceType === 'product'
      ? (productFields.data?.find((f) => f.key === row.sourceField)?.label ?? row.sourceField)
      : describeMappingTarget(form, row.sourceField).display

  const ids = mapping.rows.map((r) => r.id)
  const move = (id: string, by: -1 | 1) => reorder.mutate({ mappingId: mapping.id, rowIds: moveRow(ids, id, by) })
  const error = reorder.error ?? removeRow.error

  return (
    <div className='rounded-xl border bg-white shadow-sm'>
      <div className='border-b px-4 py-3'>
        <h3 className='text-sm font-medium'>
          Result mappings for <span className='font-mono'>{mapping.testCode}</span>
        </h3>
        <p className='text-xs text-muted-foreground'>
          Rows go out in this order (OBX-1, OBX-2, …). A row whose source is empty is left out, unless it is
          required: then the release is refused and names the row.
        </p>
      </div>

      <div className='overflow-x-persistent'>
        <table className='w-full text-sm'>
          <thead className='bg-muted/40 text-left text-xs text-muted-foreground'>
            <tr>
              <th className='px-3 py-2'>#</th>
              <th className='px-3 py-2'>Source</th>
              <th className='px-3 py-2'>Code</th>
              <th className='px-3 py-2'>Text</th>
              <th className='px-3 py-2'>Type</th>
              <th className='px-3 py-2'>Units</th>
              <th className='px-3 py-2'>Reference range</th>
              <th className='px-3 py-2'>Flag</th>
              <th className='px-3 py-2'>Required</th>
              <th className='px-3 py-2 text-right'>Actions</th>
            </tr>
          </thead>
          <tbody>
            {mapping.rows.length === 0 ? (
              <tr>
                <td colSpan={10} className='px-3 py-6 text-center text-muted-foreground'>
                  No result rows yet. Add one for each result this test sends.
                </td>
              </tr>
            ) : null}
            {mapping.rows.map((row, i) => (
              <tr key={row.id} className={cn('border-t', editing === row.id && 'bg-primary/5')}>
                <td className='px-3 py-2 text-muted-foreground'>{i + 1}</td>
                <td className='px-3 py-2'>
                  <span
                    className={cn(
                      'mr-1 rounded px-1.5 py-0.5 text-[10px] font-medium',
                      row.sourceType === 'form' ? 'bg-sky-100 text-sky-800' : 'bg-emerald-100 text-emerald-800',
                    )}
                  >
                    {row.sourceType === 'form' ? 'Form' : 'Product'}
                  </span>
                  {sourceLabel(row)}
                </td>
                <td className='px-3 py-2 font-mono'>{row.code}</td>
                <td className='px-3 py-2'>{row.text || '—'}</td>
                <td className='px-3 py-2 font-mono'>{row.valueType}</td>
                <td className='px-3 py-2'>{row.units || '—'}</td>
                <td className='px-3 py-2'>{row.referenceRange || '—'}</td>
                <td className='px-3 py-2'>{flagRuleLabel(row.flagRule)}</td>
                <td className='px-3 py-2'>{row.required ? 'Yes' : '—'}</td>
                <td className='px-3 py-2'>
                  <div className='flex justify-end gap-1'>
                    <IconButton label={`Move up ${row.code}`} disabled={i === 0 || reorder.isPending} onClick={() => move(row.id, -1)}>
                      <ArrowUp className='h-3.5 w-3.5' />
                    </IconButton>
                    <IconButton
                      label={`Move down ${row.code}`}
                      disabled={i === mapping.rows.length - 1 || reorder.isPending}
                      onClick={() => move(row.id, 1)}
                    >
                      <ArrowDown className='h-3.5 w-3.5' />
                    </IconButton>
                    <IconButton label={`Edit ${row.code}`} onClick={() => setEditing(row.id)}>
                      <Pencil className='h-3.5 w-3.5' />
                    </IconButton>
                    <IconButton
                      label={`Delete ${row.code}`}
                      disabled={removeRow.isPending}
                      onClick={() => {
                        if (!window.confirm(`Delete result row ${row.code}?`)) return
                        removeRow.mutate(row.id, { onSuccess: () => editing === row.id && setEditing(null) })
                      }}
                    >
                      <Trash2 className='h-3.5 w-3.5' />
                    </IconButton>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {error ? <p className='px-4 pt-3 text-sm text-destructive'>{error.message}</p> : null}

      <div className='p-4'>
        {editing ? (
          <RowForm
            key={editing}
            mapping={mapping}
            row={editing === 'new' ? null : (mapping.rows.find((r) => r.id === editing) ?? null)}
            form={form}
            productFields={productFields.data ?? []}
            onDone={() => setEditing(null)}
          />
        ) : (
          <Button size='sm' variant='outline' onClick={() => setEditing('new')}>
            <Plus className='mr-1 h-4 w-4' />
            Add result mapping
          </Button>
        )}
      </div>
    </div>
  )
}

function IconButton({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string
  onClick: () => void
  disabled?: boolean
  children: React.ReactNode
}) {
  return (
    <Button type='button' size='icon' variant='ghost' className='h-7 w-7' title={label} aria-label={label} disabled={disabled} onClick={onClick}>
      {children}
    </Button>
  )
}

/** Add or edit one result row. Errors show beside the field once Save is pressed. */
function RowForm({
  mapping,
  row,
  form,
  productFields,
  onDone,
}: {
  mapping: LisResultMapping
  row: LisResultMappingRow | null
  form: Parameters<typeof mappingFieldOptions>[0] | undefined
  productFields: { key: string; label: string }[]
  onDone: () => void
}) {
  const [draft, setDraft] = useState<ResultRowDraft>(() => (row ? rowDraftFrom(row) : emptyRowDraft()))
  const [submitted, setSubmitted] = useState(false)
  const add = useAddLisResultRow()
  const update = useUpdateLisResultRow()
  const pending = add.isPending || update.isPending
  const serverError = add.error ?? update.error

  const otherCodes = mapping.rows.filter((r) => r.id !== row?.id).map((r) => r.code)
  const errors = submitted ? rowDraftErrors(draft, otherCodes) : {}
  const set = <K extends keyof ResultRowDraft>(key: K, value: ResultRowDraft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }))

  // Flat value fields only: a result line carries one value, and a table is many.
  const formFields = useMemo(
    () => (form ? mappingFieldOptions(form).filter((f) => !f.isTable) : []),
    [form],
  )
  const sourceOptions =
    draft.sourceType === 'form'
      ? formFields.map((f) => ({ value: f.id, label: f.label }))
      : productFields.map((f) => ({ value: f.key, label: f.label }))

  const submit = () => {
    setSubmitted(true)
    if (Object.keys(rowDraftErrors(draft, otherCodes)).length > 0) return
    const body = rowDraftBody(draft)
    if (row) update.mutate({ rowId: row.id, body }, { onSuccess: onDone })
    else add.mutate({ mappingId: mapping.id, body }, { onSuccess: onDone })
  }

  return (
    <div className='space-y-3 rounded-lg border bg-muted/20 p-3'>
      <p className='text-sm font-medium'>{row ? `Edit result mapping ${row.code}` : 'Add result mapping'}</p>
      <div className='grid gap-3 sm:grid-cols-[140px_1fr]'>
        <div className='grid gap-1.5'>
          <Label>Source</Label>
          <Select
            value={draft.sourceType}
            // A field of the other type would not exist there, so switching clears it.
            onValueChange={(v) => setDraft((d) => ({ ...d, sourceType: v as LisResultSourceType, sourceField: '' }))}
          >
            <SelectTrigger aria-label='Source type'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='form'>Form field</SelectItem>
              <SelectItem value='product'>Product field</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className='grid gap-1.5'>
          <Label>Field</Label>
          <SearchableSelect
            value={draft.sourceField}
            onChange={(v) => set('sourceField', v)}
            options={sourceOptions}
            selectedOption={sourceOptions.find((o) => o.value === draft.sourceField)}
            placeholder={draft.sourceType === 'form' ? 'Select a form field' : 'Select a product field'}
          />
          {errors.sourceField ? <p className='text-xs text-destructive'>{errors.sourceField}</p> : null}
        </div>
      </div>

      <div className='grid gap-3 sm:grid-cols-3'>
        <TextField id='lis-row-code' label='Code (OBX-3.1)' value={draft.code} onChange={(v) => set('code', v)} error={errors.code} mono />
        <TextField id='lis-row-text' label='Text (OBX-3.2)' value={draft.text} onChange={(v) => set('text', v)} />
        <div className='grid gap-1.5'>
          <Label>Type (OBX-2)</Label>
          <Select value={draft.valueType} onValueChange={(v) => set('valueType', v as ResultRowDraft['valueType'])}>
            <SelectTrigger aria-label='Type'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {VALUE_TYPE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <TextField id='lis-row-units' label='Units (optional)' value={draft.units} onChange={(v) => set('units', v)} error={errors.units} />
        <TextField
          id='lis-row-range'
          label='Reference range (optional)'
          value={draft.referenceRange}
          onChange={(v) => set('referenceRange', v)}
          error={errors.referenceRange}
          placeholder='e.g. 6-23'
        />
        <div className='grid gap-1.5'>
          <Label>Flag (OBX-8)</Label>
          <Select value={draft.flagRule} onValueChange={(v) => set('flagRule', v as ResultRowDraft['flagRule'])}>
            <SelectTrigger aria-label='Flag'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FLAG_RULE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.flagRule ? <p className='text-xs text-destructive'>{errors.flagRule}</p> : null}
        </div>
      </div>

      <label className='flex items-start gap-2 text-sm'>
        <input
          type='checkbox'
          className='mt-0.5'
          checked={draft.required}
          onChange={(e) => set('required', e.target.checked)}
        />
        <span>
          Required
          <span className='block text-xs text-muted-foreground'>
            Off: a release with no value here goes without this line. On: the release is refused until it has a value.
          </span>
        </span>
      </label>

      {serverError ? <p className='text-sm text-destructive'>{serverError.message}</p> : null}

      <div className='flex justify-end gap-2'>
        <Button type='button' size='sm' variant='outline' onClick={onDone} disabled={pending}>
          Cancel
        </Button>
        <Button type='button' size='sm' onClick={submit} disabled={pending}>
          {pending ? <Loader2 className='mr-1 h-4 w-4 animate-spin' /> : null}
          {row ? 'Save' : 'Add'}
        </Button>
      </div>
    </div>
  )
}

function TextField({
  id,
  label,
  value,
  onChange,
  error,
  placeholder,
  mono,
}: {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  error?: string
  placeholder?: string
  mono?: boolean
}) {
  return (
    <div className='grid gap-1.5'>
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={cn(mono && 'font-mono')} />
      {error ? <p className='text-xs text-destructive'>{error}</p> : null}
    </div>
  )
}
